<template>
    <div class="form-group" :class="{ 'form-group--no-spacing': noSpacing }">
        <div class="input-group" :class="{ 'input-group--error': error }">
            <div
                class="input"
                :class="{ 'input--focus': inputFocus, 'input--filled': filled }"
                @click="toggleInputFocus(true, yearOnly ? 'year' : 'month')"
            >
                <input
                    v-if="!yearOnly"
                    :value="month"
                    @input="updateField($event, 'month')"
                    placeholder="MM"
                    type="text"
                    :id="`${name}-month`"
                    :name="`${name}-month`"
                    class="input__section input__section--month"
                    :class="{
                        'input__section--hidden': !inputFocus && !filled
                    }"
                    :disabled="disabled"
                    @focus="toggleInputFocus(true)"
                    @blur="formatInput('month')"
                    ref="month"
                />
                <span
                    v-if="!yearOnly"
                    class="input__divider"
                    :class="{
                        'input__divider--hidden': !inputFocus && !filled
                    }"
                    >/</span
                >
                <input
                    v-if="!yearOnly"
                    :value="day"
                    @input="updateField($event, 'day')"
                    placeholder="DD"
                    type="text"
                    :id="`${name}-day`"
                    :name="`${name}-day`"
                    class="input__section input__section--day"
                    :class="{
                        'input__section--hidden': !inputFocus && !filled
                    }"
                    :disabled="disabled"
                    @focus="toggleInputFocus(true)"
                    @blur="formatInput('day')"
                    ref="day"
                />
                <span
                    v-if="!fiscal && !yearOnly"
                    class="input__divider"
                    :class="{
                        'input__divider--hidden': !inputFocus && !filled
                    }"
                    >/</span
                >
                <input
                    v-if="!fiscal"
                    :value="year"
                    @input="updateField($event, 'year')"
                    placeholder="YYYY"
                    type="text"
                    :id="`${name}-year`"
                    :name="`${name}-year`"
                    class="input__section input__section--year"
                    :class="{
                        'input__section--hidden': !inputFocus && !filled
                    }"
                    :disabled="disabled"
                    @focus="toggleInputFocus(true)"
                    @blur="formatInput('year')"
                    ref="year"
                />
            </div>
            <InputLabel
                :active="inputFocus || filled"
                :name="`${name}-month`"
                :error="error"
                :disabled="disabled"
                @clicked="labelClicked"
                >{{ label }}</InputLabel
            >
        </div>
        <InputHint
            v-if="!hideHintError"
            :error="error"
            :hint="hint"
            :required="required"
            :showOptional="showOptional"
        />
    </div>
</template>

<script>
import InputHint from "./InputHint";
import InputLabel from "./InputLabel";

export default {
  name: "DateInput",
  components: {
    InputHint,
    InputLabel
  },
  props: {
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: true
    },
    focus: {
      type: Boolean,
      default: false
    },
    hint: String,
    error: String,
    required: {
      type: Boolean,
      default: false
    },
    fiscal: {
      type: Boolean,
      default: false
    },
    yearOnly: {
      type: Boolean,
      default: false
    },
    showOptional: {
      type: Boolean,
      default: false
    },
    hideHintError: {
      type: Boolean,
      default: false
    },
    noSpacing: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    forceErrorChecks: {
      type: Boolean,
      default: false
    },
    inverse: {
      type: Boolean,
      default: false
    }
  },
  created() {
    this.updateValues(this.value);
  },
  mounted() {
    if (this.focus) this.setFocus();
    this.updateError(true);
  },
  computed: {
    filled() {
      let fiscalVal = this.month.length > 0 || this.day.length > 0;
      return this.fiscal ? fiscalVal : fiscalVal || this.year.length > 0;
    },
    formattedValue() {
      if (this.yearOnly && this.year.length > 0) {
        return `${this.year}-01-01`;
      } else if (this.month.length > 0 || this.day.length > 0) {
        if (this.fiscal) {
          return `2018-${this.month}-${this.day}`;
        } else if (this.year.length > 0) {
          return `${this.year}-${this.month}-${this.day}`;
        }
      }
      return "";
    },
    invalidDay() {
      return this.yearOnly
        ? false
        : (this.day.length < 2 || parseInt(this.day) === 0) &&
                      this.day.length > 0;
    },
    invalidMonth() {
      return this.yearOnly
        ? false
        : (this.month.length < 2 || parseInt(this.month) === 0) &&
                      this.month.length > 0;
    },
    invalidYear() {
      return this.fiscal
        ? false
        : (this.year.length < 4 || parseInt(this.year) === 0) &&
                      this.year.length > 0;
    },
    showIcon() {
      return this.icon.length > 0;
    }
  },
  data() {
    return {
      day: "",
      month: "",
      year: "",
      inputFocus: this.focus,
      hadFocusOnce: false,
      hadBlurOnce: false,
      FIELDS: {
        day: {
          length: 2,
          max: 31,
          isValid: "invalidDay"
        },
        month: {
          length: 2,
          max: 12,
          isValid: "invalidMonth"
        },
        year: {
          length: 4,
          isValid: "invalidYear"
        }
      }
    };
  },
  methods: {
    formatInput(field) {
      this.toggleInputFocus(false);
      if (field === "month" || field === "day") {
        this.padInput(field);
      }
      this.updateError(true);
    },
    isValidDay() {
      const day = parseInt(this.day);
      if (this.month.length === 0) {
        return true;
      }
      const month = parseInt(this.month);
      const isLeapYear =
                this.year.length > 0 && (2000 - parseInt(this.year)) % 4 === 0;
      if (month === 2) {
        return (
          day <=
                    (this.fiscal
                      ? 28
                      : isLeapYear || this.year.length <= 0
                        ? 29
                        : 28)
        );
      } else if ([4, 6, 9, 11].includes(month)) {
        return day <= 30;
      } else {
        return day <= 31;
      }
    },
    padInput(field) {
      if (this[field].length === 1) {
        this[field] = `0${this[field]}`;
      }
    },
    setFocus() {
      if (this.$refs.month) {
        this.$refs.month.focus();
      }

      if (this.$refs.year) {
        this.$refs.year.focus();
      }
    },
    toggleInputFocus(toggle, element) {
      if (element && !this.inputFocus) {
        this.$refs[element].focus();
      }
      this.inputFocus = toggle;
    },
    updateError(switchFocus = false) {
      if (this.timeout !== null) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      this.timeout = setTimeout(() => {
        const invalidFields = [];
        const checkEmpty =
                    !switchFocus || this.hadBlurOnce || this.forceErrorChecks;
        const monthIsOnlyEmptyField =
                    this.month.length === 0 &&
                    (this.day.length > 0 ||
                        (this.fiscal ? false : this.year.length > 0));
        if (
          !this.yearOnly &&
                    (this.invalidMonth ||
                        (checkEmpty && monthIsOnlyEmptyField) ||
                        (this.forceErrorChecks &&
                            this.month.length === 0 &&
                            this.required))
        ) {
          invalidFields.push("month");
        }
        const dayIsOnlyEmptyField =
                    this.day.length === 0 &&
                    (this.month.length > 0 ||
                        (this.fiscal ? false : this.year.length > 0));
        const dayExists = this.day.length > 0 && !this.isValidDay();
        if (
          !this.yearOnly &&
                    (this.invalidDay ||
                        (checkEmpty && dayIsOnlyEmptyField) ||
                        (this.forceErrorChecks &&
                            this.day.length === 0 &&
                            this.required) ||
                        dayExists)
        ) {
          invalidFields.push("day");
        }
        const yearIsOnlyEmptyField =
                    this.year.length === 0 &&
                    !this.yearOnly &&
                    (this.month.length > 0 || this.day.length > 0);
        if (
          !this.fiscal &&
                    (this.invalidYear ||
                        (checkEmpty && yearIsOnlyEmptyField) ||
                        (this.forceErrorChecks &&
                            this.year.length === 0 &&
                            this.required))
        ) {
          invalidFields.push("year");
        }
        let error = "";
        if (invalidFields.length > 0) {
          const formattedFields = invalidFields.map((field, i) => {
            if (
              invalidFields.length > 1 &&
                            i === invalidFields.length - 1
            ) {
              return `and ${field}`;
            } else if (invalidFields.length > 2) {
              return `${field},`;
            } else {
              return field;
            }
          });
          error = `Invalid ${formattedFields.join(" ")} entered`;
        }
        this.$emit("update-error", error);
      }, 1500);
    },
    updateField(e, field) {
      this.hadFocusOnce = true;
      const { value } = e.target;
      const { length: maxLength, max, isValid } = this.FIELDS[field];
      if (value.length === 0) {
        this[field] = "";
      } else if (
        value.length <= maxLength &&
                /^\d+$/.test(value) &&
                (max === undefined || (max && parseInt(value) <= max)) &&
                (value.length !== maxLength ||
                    (value.length === maxLength && parseInt(value) > 0))
      ) {
        this[field] = value;
      } else {
        e.target.value = this[field];
      }
      this.$emit("input", this.formattedValue);
      if (!this[isValid]) {
        this.updateError(true);
      }
    },
    updateValues(value) {
      if (value.length > 0) {
        const dates = value.split("-");
        this.year = dates[0];
        this.month = dates[1];
        this.day = dates[2];
      }
    },
    labelClicked() {
      this.setFocus();
    }
  },
  watch: {
    inputFocus(newVal, oldVal) {
      this.$nextTick(() => {
        if (!this.inputFocus) {
          if (this.hadFocusOnce && oldVal) {
            this.hadBlurOnce = true;
          }
          this.updateError();
        }
      });
    },
    value(newVal) {
      this.updateValues(newVal);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";
.form-group {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: $padding-xl;

    &--no-spacing {
        margin-bottom: 0;
    }

    .input-group {
        display: flex;
        flex-direction: column;
        width: 100%;
        position: relative;

        .input {
            width: 100%;
            background-color: $white;
            color: $black;
            outline: 0;
            border: none;
            padding: 0;
            text-align: left;
            padding: $padding-input-top $padding-input-left
                $padding-input-bottom;
            border-radius: $border-radius-md;
            border: 1px solid $border-divide;
            transition: 0.2s ease-out;
            box-sizing: border-box;
            caret-color: $blue-navy;
            box-shadow: none;
            cursor: text;
            font-family: $system-font;

            &__section {
                background-color: $white;
                color: $black;
                outline: 0;
                border: none;
                padding: 0;

                font-size: $font-size-md;
                font-weight: 400;
                transition: 0.2s ease-out;
                caret-color: $blue-navy;
                opacity: 1;
                text-align: left;
                background-image: none !important;

                &--month,
                &--day {
                    width: 32px;
                }

                &--year {
                    width: 45px;
                }

                &--hidden {
                    opacity: 0;
                }
            }

            &__divider {
                font-size: $font-size-md;
                font-weight: 400;
                transition: 0.2s ease-out;
                color: $black;
                transition: 0.2s ease-out;

                &--hidden {
                    opacity: 0;
                }
            }

            &--focus {
                border-color: $blue-navy;
                background-color: $white;

                .input__section {
                    background-color: $white;
                }
            }

            &.inverse {
                background-color: $white;
            }
        }

        &--error {
            .input {
                border-color: $red-light;
            }
        }
    }
}
</style>
